// Color System
// Professional color palette inspired by modern hosting platforms

export const colors = {
  // Primary Colors - Modern Blues
  primary: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',  // Primary brand color
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a'
  },

  // Success Colors - Professional Greens
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',
    600: '#16a34a',  // Success primary
    700: '#15803d',
    800: '#166534',
    900: '#14532d'
  },

  // Warning Colors - Amber Spectrum
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',  // Warning primary
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f'
  },

  // Danger Colors - Refined Reds
  danger: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',  // Danger primary
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d'
  },

  // Neutral Grays - Optimized for interfaces
  neutral: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#e5e5e5',
    300: '#d4d4d4',
    400: '#a3a3a3',
    500: '#737373',
    600: '#525252',
    700: '#404040',
    800: '#262626',
    900: '#171717'
  },

  // UI Grays - Tailored for hosting platform
  gray: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827'
  }
} as const;

// Semantic Color Tokens
export const semanticColors = {
  // Background Colors
  background: {
    primary: colors.gray[50],      // Main app background
    secondary: '#ffffff',          // Card/panel backgrounds
    tertiary: colors.gray[100],    // Subtle backgrounds
    inverse: colors.gray[900]      // Dark backgrounds
  },

  // Surface Colors
  surface: {
    primary: '#ffffff',
    secondary: colors.gray[50],
    tertiary: colors.gray[100],
    hover: colors.gray[200],
    active: colors.gray[300],
    disabled: colors.gray[100]
  },

  // Border Colors
  border: {
    primary: colors.gray[200],
    secondary: colors.gray[300],
    hover: colors.gray[400],
    focus: colors.primary[500],
    error: colors.danger[300],
    success: colors.success[300]
  },

  // Text Colors
  text: {
    primary: colors.gray[900],     // Main text
    secondary: colors.gray[600],   // Secondary text
    tertiary: colors.gray[500],    // Muted text
    inverse: '#ffffff',            // Text on dark backgrounds
    disabled: colors.gray[400],    // Disabled text
    link: colors.primary[600],     // Links
    linkHover: colors.primary[700] // Link hover state
  },

  // Status Colors
  status: {
    // Server Running - Success Green
    running: {
      background: colors.success[100],
      border: colors.success[200],
      text: colors.success[800],
      indicator: colors.success[500]
    },
    // Server Stopped - Neutral Gray
    stopped: {
      background: colors.gray[100],
      border: colors.gray[200],
      text: colors.gray[700],
      indicator: colors.gray[500]
    },
    // Server Starting - Warning Amber
    starting: {
      background: colors.warning[100],
      border: colors.warning[200],
      text: colors.warning[800],
      indicator: colors.warning[500]
    },
    // Server Error - Danger Red
    error: {
      background: colors.danger[100],
      border: colors.danger[200],
      text: colors.danger[800],
      indicator: colors.danger[500]
    }
  }
} as const;

// Dark Mode Color Variants
export const darkColors = {
  background: {
    primary: colors.gray[900],
    secondary: colors.gray[800],
    tertiary: colors.gray[700],
    inverse: colors.gray[50]
  },
  surface: {
    primary: colors.gray[800],
    secondary: colors.gray[700],
    tertiary: colors.gray[600],
    hover: colors.gray[600],
    active: colors.gray[500],
    disabled: colors.gray[700]
  },
  border: {
    primary: colors.gray[700],
    secondary: colors.gray[600],
    hover: colors.gray[500],
    focus: colors.primary[400],
    error: colors.danger[600],
    success: colors.success[600]
  },
  text: {
    primary: colors.gray[50],
    secondary: colors.gray[300],
    tertiary: colors.gray[400],
    inverse: colors.gray[900],
    disabled: colors.gray[500],
    link: colors.primary[400],
    linkHover: colors.primary[300]
  }
} as const;